/**
 * Settings › Hosts: machines that report for themselves (PLAN-2026-09-15 D1).
 *
 * A host is an asset whose driver is one of the agent drivers: `cosmon-agent`
 * (a server), `cosmon-agent-nvr` (a machine whose disks hold footage),
 * `cosmon-agent-desktop` or `cosmon-agent-laptop`. It has no probe:
 * everything arrives from an agent installed on it, so the only thing to do
 * here is enrol one, watch that it is reporting, and stop it.
 *
 * Built for hundreds of hosts (2026-10-21): it opens By site, worst first,
 * with healthy sites folded, and each site's NX setup in its header (the
 * separate "NX sites" list is gone). Search, Needs attention and a site
 * picker narrow it, and ride in the URL. The rules are in `hosts-view.ts`.
 */

import { Copy, Check, Download, MapPin, Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AgentLogDrawer } from './AgentLog.tsx';
import { NxActionsDrawer } from './NxActions.tsx';
import { NxCamerasDrawer } from './NxCameras.tsx';
import { NxLoginsDrawer } from './NxLogins.tsx';
import { NxSiteSetupDrawer } from './NxSiteSetup.tsx';
import {
  chipCounts,
  formatFilter,
  hostSeverity,
  hostSite,
  hostState,
  parseFilter,
  siteGroups,
  siteOptions,
  visible,
  type HostFilter,
  type Severity,
  type SiteGroup,
} from './hosts-view.ts';
import { loginSummary, nxSites, siteKey, siteMates, siteState, type HostLogins, type NxSite } from './nx-logins.ts';
import { ApiError, api } from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  Drawer,
  Empty,
  FilterChip,
  Loading,
  Pill,
  Section,
  Toolbar,
  type ColumnDef,
} from './components/index.ts';

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

interface Host {
  id: string;
  code: string | null;
  name: string;
  kind: string;
  driverId: string;
  /** The CosMon site the host asset is on; null when none is set. */
  site: { id: string; code: string | null; name: string } | null;
  agentId: string | null;
  agentVersion: string | null;
  os: string | null;
  arch: string | null;
  enrolledAt: string | null;
  lastReportAt: string | null;
  updateChannel: 'stable' | 'canary' | 'off' | null;
  enrolmentPending: boolean;
  enrolmentExpiresAt: string | null;
  /** What the agent found of NX Witness (064); null when none. */
  nx: HostNx | null;
  /** The elevated NX login (065), or null: actions then use the monitoring login. */
  nxElevatedCredentialId: string | null;
  /** Both NX logins by name: the asset's own credential, and the elevated one. */
  nxLogins: HostLogins;
  /** NX auto-add (072) for the host's NX site; null when its agent has not said which site. */
  nxAutoAdd: boolean | null;
}

interface HostNx {
  service: string | null;
  serviceState: string | null;
  port: number | null;
  version: string | null;
  brand: string | null;
  serverName?: string | null;
  siteName?: string | null;
  /** NX's own site id (newer agents): servers sharing it are offered one login. */
  siteId?: string | null;
  reachable: boolean;
  login: 'none' | 'ok' | 'failed' | 'cert_changed';
  /** The monitoring login reached a Power-User-only page (065). */
  loginPower: boolean | null;
  error: string | null;
}

/** NX reports its build's brand as a key (the first real server said `hdwitness`). */
const NX_BRANDS: Record<string, string> = {
  hdwitness: 'HD Witness',
  nxwitness: 'Nx Witness',
  digitalwatchdog: 'DW Spectrum',
  hanwha: 'Wisenet WAVE',
};

function ago(iso: string | null): string {
  if (iso === null) return 'never';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 90) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const state = (host: Host): ReturnType<typeof hostState> => hostState(host, Date.now());

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

const PILL: Record<Severity, 'ok' | 'warning' | 'fault'> = { ok: 'ok', warn: 'warning', fault: 'fault' };
const SEVERITY_ORDER: Record<Severity, number> = { fault: 0, warn: 1, ok: 2 };

/** The filter lives after the hash's `?`; read once on load, written without a history entry per keystroke. */
const filterFromUrl = (): HostFilter => parseFilter(window.location.hash.split('?')[1] ?? '');

export function HostsView(): React.JSX.Element {
  const [hosts, setHosts] = useState<Host[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState<Host | null>(null);
  const [logFor, setLogFor] = useState<Host | null>(null);
  const [surveyFor, setSurveyFor] = useState<Host | null>(null);
  const [actionsFor, setActionsFor] = useState<Host | null>(null);
  const [camerasFor, setCamerasFor] = useState<Host | null>(null);
  const [loginsFor, setLoginsFor] = useState<Host | null>(null);
  // The NX site being set up, by its key: the drawer reads the site afresh from each hosts load.
  const [siteFor, setSiteFor] = useState<string | null>(null);
  const [filter, setFilterState] = useState<HostFilter>(filterFromUrl);
  const setFilter = useCallback((next: Partial<HostFilter>) => {
    setFilterState((current) => {
      const f = { ...current, ...next };
      // replaceState, not the hash: typing in the search box is not a page to go Back to.
      window.history.replaceState(null, '', `#/settings/hosts${formatFilter(f)}`);
      return f;
    });
  }, []);
  // A link followed while the page is open (the Hosts tab, a pasted URL) changes the hash, not the mount.
  useEffect(() => {
    const onHash = (): void => setFilterState(filterFromUrl());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const load = useCallback(async () => {
    try {
      setHosts((await api.get<{ hosts: Host[] }>('/api/agents')).hosts);
      setError(null);
    } catch (err) {
      setError(failure(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Every NX site from every host, so a site set up from a filtered view still covers all its servers.
  const allNx = useMemo(() => new Map(nxSites(hosts ?? []).map((x) => [x.key, x])), [hosts]);

  const columns = useMemo<ColumnDef<Host, unknown>[]>(
    () => [
      {
        id: 'state',
        header: 'State',
        // Worst first, like By site: a quiet agent before one not enrolled yet.
        accessorFn: (h) => `${SEVERITY_ORDER[hostSeverity(h, Date.now())]}${state(h).label}`,
        cell: ({ row }) => {
          const s = state(row.original);
          return (
            <Pill state={s.tone} reason={s.reason}>
              {s.label}
            </Pill>
          );
        },
        size: 120,
      },
      {
        id: 'host',
        header: 'Host',
        accessorFn: (h) => (h.code ?? h.name).toLowerCase(),
        cell: ({ row }) => (
          <div>
            <div className="cell-strong">{row.original.code ?? row.original.name}</div>
            <div className="small muted">
              {row.original.kind}
              {row.original.os !== null && ` · ${row.original.os}/${row.original.arch ?? '?'}`}
            </div>
          </div>
        ),
      },
      {
        id: 'site',
        header: 'Site',
        accessorFn: (h) => hostSite(h).label.toLowerCase(),
        cell: ({ row }) => {
          const site = hostSite(row.original);
          const key = siteKey(row.original);
          const nx = key === null ? undefined : allNx.get(key);
          return (
            <div className="stack-tight">
              <span className={site.from === 'cosmon' ? 'small' : 'small muted'}>{site.label}</span>
              {nx !== undefined && (
                <span className="small">
                  <button type="button" className="agent-log-pick" disabled={nx.siteId === null} onClick={() => setSiteFor(nx.key)}>
                    {nx.missing === nx.hosts.length ? 'Set up NX site' : 'Change site logins'}
                  </button>
                </span>
              )}
            </div>
          );
        },
        size: 180,
      },
      {
        id: 'agent',
        header: 'Agent',
        accessorFn: (h) => h.agentVersion ?? '',
        cell: ({ row }) => (
          <div className="stack-tight">
            <span className="small muted mono">
              {row.original.agentId === null ? '—' : `${row.original.agentVersion ?? '?'} · ${row.original.agentId}`}
            </span>
            {row.original.nx !== null && (
              <span className="small" title={row.original.nx.error ?? undefined}>
                {NX_BRANDS[row.original.nx.brand ?? ''] ?? row.original.nx.brand ?? 'NX Witness'} {row.original.nx.version ?? ''}
                {!row.original.nx.reachable && ` · ${row.original.nx.serviceState ?? 'not answering'}`}
                {row.original.nx.siteName != null && ` · site ${row.original.nx.siteName}`}
              </span>
            )}
            {row.original.nx !== null && (
              <span className="small">
                {(() => {
                  const s = loginSummary(row.original);
                  return s.problem ? <strong className="text-warn">{s.text}</strong> : s.text;
                })()}
                {row.original.nxLogins.elevated !== null && ` · elevated: ${row.original.nxLogins.elevated.username ?? '?'}`}{' '}
                <button type="button" className="agent-log-pick" onClick={() => setLoginsFor(row.original)}>
                  Change logins
                </button>
                {row.original.nx.loginPower === true && (
                  <strong className="text-warn" title="The monitoring login is held on the host all the time: give it view-only rights, and use an elevated login for NX actions.">
                    {' · monitoring login can change NX'}
                  </strong>
                )}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'channel',
        header: 'Updates',
        accessorFn: (h) => h.updateChannel ?? '',
        cell: ({ row }) =>
          row.original.agentId === null ? (
            <span className="small muted">—</span>
          ) : (
            <select
              aria-label={`Update channel for ${row.original.code ?? row.original.name}`}
              title="stable: what customers run · canary: gets a release first · off: never updated automatically"
              value={row.original.updateChannel ?? 'stable'}
              onChange={async (e) => {
                try {
                  await api.put(`/api/agents/${row.original.agentId}/channel`, { channel: e.target.value });
                  await load();
                } catch (err) {
                  setError(failure(err));
                }
              }}
            >
              <option value="stable">stable</option>
              <option value="canary">canary</option>
              <option value="off">off</option>
            </select>
          ),
        size: 110,
      },
      {
        id: 'reported',
        header: 'Last report',
        accessorFn: (h) => h.lastReportAt ?? '',
        cell: ({ row }) => <span className="small">{ago(row.original.lastReportAt)}</span>,
        size: 130,
      },
      {
        id: 'act',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="row-tight">
            <Button size="sm" onClick={() => setEnrolling(row.original)}>
              {row.original.agentId === null ? 'Enrol' : 'Re-enrol'}
            </Button>
            {row.original.agentId !== null && (
              <Button size="sm" variant="ghost" onClick={() => setLogFor(row.original)}>
                Get log
              </Button>
            )}
            {row.original.agentId !== null && row.original.nx !== null && (
              <Button size="sm" variant="ghost" onClick={() => setSurveyFor(row.original)}>
                Get NX survey
              </Button>
            )}
            {row.original.agentId !== null && row.original.nx !== null && (
              <Button size="sm" variant="ghost" onClick={() => setActionsFor(row.original)}>
                NX actions
              </Button>
            )}
            {row.original.agentId !== null && row.original.nx !== null && (
              <Button size="sm" variant="ghost" onClick={() => setCamerasFor(row.original)}>
                NX cameras
              </Button>
            )}
            {row.original.agentId !== null && (
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  try {
                    await api.post(`/api/agents/${row.original.agentId}/revoke`);
                    await load();
                  } catch (err) {
                    setError(failure(err));
                  }
                }}
              >
                Revoke
              </Button>
            )}
          </div>
        ),
        size: 230,
      },
    ],
    [load, allNx],
  );
  const groupColumns = useMemo(() => columns.filter((c) => c.id !== 'site'), [columns]);

  if (error !== null && hosts === null) return <Banner tone="fault">{error}</Banner>;
  if (hosts === null) return <Loading rows={3} cols={5} label="Loading hosts" />;
  const openSite: NxSite<Host> | null = siteFor === null ? null : (allNx.get(siteFor) ?? null);
  const now = Date.now();
  const shown = visible(hosts, filter, now);
  const counts = chipCounts(hosts, filter, now);
  const options = siteOptions(hosts);
  const narrowed = filter.q.trim() !== '' || filter.site !== null || filter.show !== 'all';
  const groups = filter.by === 'sites' ? siteGroups(hosts, shown, now) : [];
  const trouble = groups.filter((g) => g.severity !== 'ok');
  const healthy = groups.filter((g) => g.severity === 'ok');

  const group = (g: SiteGroup<Host>): React.JSX.Element => (
    <Section
      flush
      key={g.site.key}
      title={
        <span className="row-tight">
          <Pill state={PILL[g.severity]}>{g.severity === 'ok' ? 'ok' : g.severity === 'warn' ? 'attention' : 'fault'}</Pill>
          {g.site.label}
        </span>
      }
      summary={`${plural(g.hosts.length, 'host')}${g.problems > 0 ? ` · ${g.problems} need${g.problems === 1 ? 's' : ''} attention` : ''}`}
    >
      {g.nx.length > 0 && (
        <ul className="check-list host-site-nx">
          {g.nx.map((site) => {
            const st = siteState(site);
            return (
              <li key={site.key}>
                <Pill state={st.tone === 'ok' ? 'ok' : st.tone === 'warn' ? 'warning' : 'fault'}>NX</Pill>
                <span className="stack-tight">
                  <span>
                    <span className="cell-strong">{site.name}</span>
                    <span className="small muted">
                      {' · '}
                      {site.hosts.map((h) => h.code ?? h.name).join(', ')}
                    </span>
                  </span>
                  <span className={`small ${st.tone === 'ok' ? 'muted' : 'text-warn'}`}>{st.text}</span>
                </span>
                <Button
                  size="sm"
                  variant={st.tone === 'ok' ? 'ghost' : 'primary'}
                  disabled={site.siteId === null}
                  title={site.siteId === null ? 'These agents do not say which NX site they are on: update them first.' : undefined}
                  onClick={() => setSiteFor(site.key)}
                >
                  {site.missing === site.hosts.length ? 'Set up site' : 'Change site logins'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      <DataTable data={g.hosts} columns={groupColumns} getRowId={(h) => h.id} caption={`Hosts at ${g.site.label}`} />
    </Section>
  );

  return (
    <>
      {error !== null && <Banner tone="fault">{error}</Banner>}
      <Toolbar>
        <label className="search-field">
          <Search size={14} aria-hidden="true" />
          <input
            type="search"
            placeholder="Filter by host, site or version"
            aria-label="Filter hosts"
            value={filter.q}
            onChange={(e) => setFilter({ q: e.target.value })}
          />
        </label>
        <SitePicker options={options} value={filter.site} onChange={(site) => setFilter({ site })} />
        <div className="chip-group" role="group" aria-label="Show">
          <FilterChip active={filter.show === 'all'} onClick={() => setFilter({ show: 'all' })}>
            All <span className="chip-count">{counts.all}</span>
          </FilterChip>
          <FilterChip active={filter.show === 'attention'} onClick={() => setFilter({ show: filter.show === 'attention' ? 'all' : 'attention' })}>
            Needs attention <span className="chip-count">{counts.attention}</span>
          </FilterChip>
          <FilterChip active={filter.show === 'nx'} onClick={() => setFilter({ show: filter.show === 'nx' ? 'all' : 'nx' })}>
            NX <span className="chip-count">{counts.nx}</span>
          </FilterChip>
        </div>
        <div className="chip-group" role="group" aria-label="Group">
          <FilterChip active={filter.by === 'sites'} onClick={() => setFilter({ by: 'sites' })}>
            By site
          </FilterChip>
          <FilterChip active={filter.by === 'hosts'} onClick={() => setFilter({ by: 'hosts' })}>
            All hosts
          </FilterChip>
        </div>
        <span className="small muted">
          {shown.length === hosts.length ? plural(hosts.length, 'host') : `${shown.length} of ${plural(hosts.length, 'host')}`}
        </span>
      </Toolbar>
      {hosts.length === 0 ? (
        <Section>
          <Empty
            title="No hosts yet"
            body="Create an asset of kind Server, NVR, Desktop PC or Laptop with its COSMON host agent driver (a machine whose disks hold footage is an NVR), and it appears here to enrol."
          />
        </Section>
      ) : shown.length === 0 ? (
        <Section>
          <Empty title="No host matches" body="Clear the search, the site or the chip to see more." />
        </Section>
      ) : filter.by === 'hosts' ? (
        <Section flush title="Hosts" summary="A host reports for itself: a Windows or Linux machine with the COSMON agent installed.">
          <DataTable
            data={shown}
            columns={columns}
            getRowId={(h) => h.id}
            initialSort={[{ id: 'state', desc: false }]}
            caption="Hosts running the agent"
          />
        </Section>
      ) : (
        <>
          {trouble.map(group)}
          {healthy.length > 0 &&
            (narrowed || trouble.length === 0 ? (
              healthy.map(group)
            ) : (
              <details className="fold">
                <summary>{plural(healthy.length, 'site')} with every host ok</summary>
                <div className="fold-body">{healthy.map(group)}</div>
              </details>
            ))}
        </>
      )}
      {enrolling !== null && (
        <EnrolDrawer host={enrolling} onClose={() => setEnrolling(null)} onEnrolled={() => void load()} />
      )}
      {logFor !== null && logFor.agentId !== null && (
        <AgentLogDrawer agentId={logFor.agentId} label={logFor.code ?? logFor.name} onClose={() => setLogFor(null)} />
      )}
      {surveyFor !== null && surveyFor.agentId !== null && (
        <AgentLogDrawer
          kind="nx_survey"
          agentId={surveyFor.agentId}
          label={surveyFor.code ?? surveyFor.name}
          onClose={() => setSurveyFor(null)}
          logins={surveyFor.nxLogins}
          onChangeLogin={() => {
            setSurveyFor(null);
            setLoginsFor(surveyFor);
          }}
        />
      )}
      {actionsFor !== null && actionsFor.agentId !== null && (
        <NxActionsDrawer
          agentId={actionsFor.agentId}
          label={actionsFor.code ?? actionsFor.name}
          elevatedCredentialId={actionsFor.nxElevatedCredentialId}
          loginPower={actionsFor.nx?.loginPower ?? null}
          onClose={() => setActionsFor(null)}
          onChanged={() => void load()}
          logins={actionsFor.nxLogins}
          onChangeLogin={() => {
            setActionsFor(null);
            setLoginsFor(actionsFor);
          }}
        />
      )}
      {loginsFor !== null && (
        <NxLoginsDrawer
          key={loginsFor.id}
          host={loginsFor}
          mates={siteMates(loginsFor, hosts)}
          siteName={loginsFor.nx?.siteName ?? null}
          onClose={() => setLoginsFor(null)}
          onSaved={() => void load()}
        />
      )}
      {openSite !== null && (
        <NxSiteSetupDrawer key={openSite.key} site={openSite} onClose={() => setSiteFor(null)} refresh={load} />
      )}
      {camerasFor !== null && (
        <NxCamerasDrawer assetId={camerasFor.id} label={camerasFor.code ?? camerasFor.name} onClose={() => setCamerasFor(null)} />
      )}
    </>
  );
}

/**
 * A site by typing part of its name: hundreds of sites do not fit a dropdown.
 * Picks only a site that exists; clearing the box shows every site again.
 */
function SitePicker({
  options,
  value,
  onChange,
}: {
  options: ReturnType<typeof siteOptions>;
  value: string | null;
  onChange: (site: string | null) => void;
}): React.JSX.Element {
  const picked = options.find((o) => o.key === value) ?? null;
  const [text, setText] = useState(picked?.label ?? '');
  useEffect(() => setText(picked?.label ?? ''), [picked?.label]);
  return (
    <label className="search-field">
      <MapPin size={14} aria-hidden="true" />
      <input
        type="search"
        list="host-sites"
        placeholder="Any site"
        aria-label="Site"
        value={text}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          if (t === '') onChange(null);
          else {
            const hit = options.find((o) => o.label.toLowerCase() === t.toLowerCase());
            if (hit !== undefined) onChange(hit.key);
          }
        }}
      />
      <datalist id="host-sites">
        {options.map((o) => (
          <option key={o.key} value={o.label}>
            {plural(o.count, 'host')}
          </option>
        ))}
      </datalist>
    </label>
  );
}

function EnrolDrawer({
  host,
  onClose,
  onEnrolled,
}: {
  host: Host;
  onClose: () => void;
  onEnrolled: () => void;
}): React.JSX.Element {
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  // The same enrolment, short enough to type (062): an hour, not a day.
  const [code, setCode] = useState<string | null>(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const server = `${window.location.origin}/agent`;
  /*
   * Linux: fetch the stable binary with the token as the credential (the host
   * has no secret yet), then let `install` enrol, install and start the
   * service. `uname -m` goes in as-is; the daemon knows x86_64 and aarch64.
   */
  const credential = code ?? token;
  const linux =
    credential === null
      ? ''
      : `curl -fsSLo /tmp/cosmon-agent -H 'X-Cosmon-Enrolment: ${credential}' ${server}/bootstrap/linux/$(uname -m) && ` +
        `chmod +x /tmp/cosmon-agent && sudo /tmp/cosmon-agent install --server ${server} ${code !== null ? '--code' : '--token'} ${credential}`;

  /*
   * Windows from a command line: the Linux command's twin, for an elevated
   * PowerShell — the same token-gated bootstrap download, then `install`.
   * Added while testing (owner, 2026-09-29) because the unsigned installer is
   * blocked by browsers and Defender; revisit once releases are
   * Authenticode-signed. TLS 1.2 is set because Windows PowerShell 5.1 on
   * Server 2016 does not offer it by default.
   */
  const windowsCommand =
    credential === null
      ? ''
      : `$c='${credential}'; [Net.ServicePointManager]::SecurityProtocol='Tls12'; ` +
        `iwr -UseBasicParsing -Headers @{'X-Cosmon-Enrolment'=$c} ${server}/bootstrap/windows/x86_64 -OutFile "$env:TEMP\\cosmon-agent.exe"; ` +
        `& "$env:TEMP\\cosmon-agent.exe" install --server ${server} ${code !== null ? '--code' : '--token'} $c`;

  /*
   * The same, for Command Prompt (owner, 2026-09-30: "i will need to run the
   * commands in cmd"). curl ships with Windows 10 1803+ and Server 2019+;
   * `&&` so a failed download never runs a stale exe left in %TEMP%.
   */
  const cmdCommand =
    credential === null
      ? ''
      : `curl -fsSL -H "X-Cosmon-Enrolment: ${credential}" -o "%TEMP%\\cosmon-agent.exe" ${server}/bootstrap/windows/x86_64 && ` +
        `"%TEMP%\\cosmon-agent.exe" install --server ${server} ${code !== null ? '--code' : '--token'} ${credential}`;

  /*
   * Windows: the setup program, saved under a name that carries the token —
   * the installer reads its own file name, so whoever double-clicks it types
   * nothing (owner, 2026-09-22: clients never run commands). Renaming a signed
   * file does not break its signature; one build serves every host.
   */
  const downloadInstaller = async (): Promise<void> => {
    if (token === null) return;
    setDownloading(true);
    setError(null);
    try {
      const res = await fetch('/agent/bootstrap/windows/installer', { headers: { 'X-Cosmon-Enrolment': token } });
      if (!res.ok) throw new Error(res.status === 404 ? 'No Windows installer has been published yet.' : `The download failed (${res.status}).`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = `COSMON-Agent-Setup_${token}.exe`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError(failure(err));
    } finally {
      setDownloading(false);
    }
  };

  const copy = (label: string, text: string): void => {
    void navigator.clipboard?.writeText(text).then(() => setCopied(label));
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={`Enrol ${host.code ?? host.name}`}
      subtitle="Shown once. It works for one host: the code for an hour, the installer for a day."
    >
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {token === null ? (
          <>
            <p className="small muted invite-lede">
              {host.agentId === null
                ? 'This issues a short code to type into the installer, a Windows installer with the enrolment built in, and one command for Linux.'
                : 'Re-enrolling issues a new identity. The existing agent keeps reporting until you revoke it.'}
            </p>
            <Button
              variant="primary"
              busy={busy}
              icon={<Plus size={14} />}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  const res = await api.post<{ token: string; expiresAt: string; code?: string; codeExpiresAt?: string }>(
                    `/api/assets/${host.id}/agent-enrolment`,
                  );
                  setToken(res.token);
                  setExpiresAt(res.expiresAt);
                  setCode(res.code ?? null);
                  setCodeExpiresAt(res.codeExpiresAt ?? null);
                  onEnrolled();
                } catch (err) {
                  setError(failure(err));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Enrol this host
            </Button>
          </>
        ) : (
          <>
            <Banner tone="ok" title="Install it on the host">
              Usable once. The code works until{' '}
              {codeExpiresAt === null ? 'an hour from now' : new Date(codeExpiresAt).toLocaleTimeString()}; the installer
              download and the token until {expiresAt === null ? 'this time tomorrow' : new Date(expiresAt).toLocaleString()}.
            </Banner>
            <p className="small muted">
              If it runs NX Witness: once it reports, use <strong>Change logins</strong> on its row to give the agent its NX
              login. Servers on the same NX site can be given one login together.
            </p>
            {code !== null && (
              <div className="stack-tight invite-link">
                <strong className="small">Enrolment code</strong>
                <output className="enrol-code" aria-label="Enrolment code">
                  {code}
                </output>
                <Button size="sm" icon={copied === 'code' ? <Check size={14} /> : <Copy size={14} />} onClick={() => copy('code', code)}>
                  {copied === 'code' ? 'Copied' : 'Copy code'}
                </Button>
                <span className="small muted">
                  Read it out or type it: the Windows installer asks for it, and on Linux it goes after --code. No 0/O or
                  1/I/L to confuse, and case does not matter.
                </span>
              </div>
            )}
            <div className="stack-tight invite-link">
              <strong className="small">Windows</strong>
              <Button variant="primary" size="sm" icon={<Download size={14} />} busy={downloading} onClick={() => void downloadInstaller()}>
                Download the installer
              </Button>
              <span className="small muted">
                Send it to whoever is at the machine: they double-click it and click Next. The enrolment is in the file
                name, so keep the name as it is — or, if it gets renamed, they type the code when it asks.
              </span>
            </div>
            <div className="stack-tight invite-link">
              <strong className="small">Windows (PowerShell)</strong>
              <input
                readOnly
                value={windowsCommand}
                aria-label="Windows PowerShell install command"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button
                size="sm"
                icon={copied === 'windows' ? <Check size={14} /> : <Copy size={14} />}
                onClick={() => copy('windows', windowsCommand)}
              >
                {copied === 'windows' ? 'Copied' : 'Copy command'}
              </Button>
              <span className="small muted">
                Paste into PowerShell opened with Run as administrator. Use it where the installer download is blocked
                (it is not code-signed yet).
              </span>
            </div>
            <div className="stack-tight invite-link">
              <strong className="small">Windows (Command Prompt)</strong>
              <input
                readOnly
                value={cmdCommand}
                aria-label="Windows Command Prompt install command"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button
                size="sm"
                icon={copied === 'cmd' ? <Check size={14} /> : <Copy size={14} />}
                onClick={() => copy('cmd', cmdCommand)}
              >
                {copied === 'cmd' ? 'Copied' : 'Copy command'}
              </Button>
              <span className="small muted">Paste into Command Prompt opened with Run as administrator.</span>
            </div>
            <div className="stack-tight invite-link">
              <strong className="small">Linux</strong>
              <input readOnly value={linux} aria-label="Linux install command" onFocus={(e) => e.currentTarget.select()} />
              <Button size="sm" icon={copied === 'linux' ? <Check size={14} /> : <Copy size={14} />} onClick={() => copy('linux', linux)}>
                {copied === 'linux' ? 'Copied' : 'Copy command'}
              </Button>
              <span className="small muted">
                Run it on the host as a user who can sudo. TrueNAS SCALE is different — see the agent README.
              </span>
            </div>
            <div className="stack-tight invite-link">
              <strong className="small">Token (for scripts and RMM tools)</strong>
              <input readOnly value={token} aria-label="Enrolment token" onFocus={(e) => e.currentTarget.select()} />
              <Button size="sm" icon={copied === 'token' ? <Check size={14} /> : <Copy size={14} />} onClick={() => copy('token', token)}>
                {copied === 'token' ? 'Copied' : 'Copy token'}
              </Button>
              <span className="small muted">
                Shown once and not readable again. Issue another if it is lost — the old one stops working.
              </span>
            </div>
            <div className="row">
              <Button onClick={onClose}>Done</Button>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
